import React, { useEffect, useMemo, useRef, useState } from 'react';
import './LiquidWidget.css';
import {
  buildWavePath,
  clamp,
  colorWithAlpha,
  formatDisplayValue,
  mixColors,
  parseColorToRgb,
  pickReadableColor,
  resolveLiquidColor,
} from './liquidShared';

const LIQUID_SHAPE_PATH =
  'M60 18A42 42 0 1 1 60 102A42 42 0 1 1 60 18Z';
const LEGACY_CARD_BACKGROUND =
  'linear-gradient(180deg, rgba(7, 18, 48, 0.98) 0%, rgba(7, 18, 46, 0.95) 100%)';
const LIQUID_EMPTY_SURFACE_Y = 92;
const LIQUID_FULL_SURFACE_Y = 8;
const THEME_HIGHLIGHT_COLOR = 'var(--part-color)';
const THEME_TEXT_COLOR = 'var(--widget-title-color)';
const THEME_SHADOW_COLOR = 'var(--shadow-black-soft)';
const LEGACY_LIQUID_TITLE_COLORS = ['rgba(238, 245, 255, 0.92)', '#FFFFFF', 'rgba(255, 255, 255, 0.96)', 'rgba(255, 255, 255, 1)'];
const LEGACY_LIQUID_VALUE_COLORS = ['#F3F9FF', '#FFFFFF', 'rgba(243, 249, 255, 1)', 'rgba(255, 255, 255, 1)'];
const LEGACY_LIQUID_UNIT_COLORS = ['rgba(222, 234, 255, 0.76)', '#FFFFFF', 'rgba(255, 255, 255, 0.84)'];

const colorsEqual = (left, right) => {
  const rgbLeft = parseColorToRgb(left);
  const rgbRight = parseColorToRgb(right);

  if (!rgbLeft || !rgbRight) {
    return String(left || '').trim() === String(right || '').trim();
  }

  return rgbLeft.every((channel, index) => channel === rgbRight[index]);
};

const matchesAnyColor = (value, candidates) =>
  candidates.some((candidate) => colorsEqual(value, candidate));

const LiquidWidget = ({
  id,
  title,
  transparentBackground,
  min,
  max,
  value,
  unit,
  showUnit = true,
  color,
  colors,
  changeColorBasedOnValue = false,
  colorGrading = false,
  cardBackgroundColor,
  titleColor,
  valueColor,
  unitColor,
  glowColor,
  draggable,
  dims,
  decimals,
}) => {
  const [cursorState, setCursorState] = useState('idle');
  const [animatedValue, setAnimatedValue] = useState(0);
  const animationFrameRef = useRef(null);
  const previousValueRef = useRef(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const minNum = useMemo(() => {
    const numeric = Number(min);
    return Number.isFinite(numeric) ? numeric : 0;
  }, [min]);

  const maxNum = useMemo(() => {
    const numeric = Number(max);
    if (Number.isFinite(numeric)) {
      return numeric >= minNum ? numeric : minNum;
    }
    return Math.max(minNum + 100, 100);
  }, [max, minNum]);

  const targetValue = useMemo(() => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return minNum;
    return clamp(numeric, minNum, maxNum);
  }, [maxNum, minNum, value]);

  useEffect(() => {
    if (prefersReducedMotion) {
      setAnimatedValue(targetValue);
      previousValueRef.current = targetValue;
      return undefined;
    }

    const startValue = Number.isFinite(previousValueRef.current) ? previousValueRef.current : minNum;
    const delta = targetValue - startValue;
    const duration = Math.min(1400, Math.max(550, Math.abs(delta) * 14));
    const startedAt = performance.now();

    const tick = (timestamp) => {
      const elapsed = timestamp - startedAt;
      const progress = clamp(elapsed / duration, 0, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = startValue + delta * eased;

      setAnimatedValue(nextValue);

      if (progress < 1) {
        animationFrameRef.current = window.requestAnimationFrame(tick);
      } else {
        previousValueRef.current = targetValue;
      }
    };

    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [minNum, prefersReducedMotion, targetValue]);

  const normalizedValue = useMemo(() => {
    const range = maxNum - minNum;
    if (!(range > 0)) return 0;
    return clamp((animatedValue - minNum) / range, 0, 1);
  }, [animatedValue, maxNum, minNum]);

  const activeLiquidColor = useMemo(
    () =>
      resolveLiquidColor({
        color,
        value: animatedValue,
        min: minNum,
        max: maxNum,
        changeColorBasedOnValue,
        colorGrading,
        colors,
      }),
    [animatedValue, changeColorBasedOnValue, color, colorGrading, colors, maxNum, minNum]
  );

  const width = Number.isFinite(Number(dims?.width)) ? Number(dims.width) : 268;
  const height = Number.isFinite(Number(dims?.height)) ? Number(dims.height) : 268;

  const sceneSize = useMemo(
    () => Math.round(Math.max(132, Math.min(width * 0.72, height * 0.86, 228))),
    [height, width]
  );

  const svgIdBase = useMemo(
    () => String(id || 'liquid-widget').replace(/[^A-Za-z0-9_-]/g, '_'),
    [id]
  );
  const clipId = `${svgIdBase}-clip`;
  const fillGradientId = `${svgIdBase}-fill-gradient`;
  const fillGradientBackId = `${svgIdBase}-fill-gradient-back`;

  const surfaceY = useMemo(() => {
    const liftedProgress = Math.pow(normalizedValue, 1.45);
    return clamp(
      LIQUID_EMPTY_SURFACE_Y -
        liftedProgress * (LIQUID_EMPTY_SURFACE_Y - LIQUID_FULL_SURFACE_Y),
      LIQUID_FULL_SURFACE_Y,
      LIQUID_EMPTY_SURFACE_Y
    );
  }, [normalizedValue]);
  const waveAmplitude = useMemo(() => 4.2 + normalizedValue * 3.8, [normalizedValue]);
  const isNearFull = normalizedValue >= 0.985;
  const frontWavePath = useMemo(
    () => buildWavePath({ amplitude: waveAmplitude, baseline: 58, invert: false }),
    [waveAmplitude]
  );
  const backWavePath = useMemo(
    () => buildWavePath({ amplitude: waveAmplitude * 0.72, baseline: 58, invert: true }),
    [waveAmplitude]
  );

  const liquidBrightColor = useMemo(
    () => mixColors(activeLiquidColor, THEME_HIGHLIGHT_COLOR, 0.22),
    [activeLiquidColor]
  );
  const liquidDeepColor = useMemo(
    () => mixColors(activeLiquidColor, THEME_SHADOW_COLOR, 0.18),
    [activeLiquidColor]
  );
  const glowTone = useMemo(
    () => String(glowColor || '').trim() || colorWithAlpha(activeLiquidColor, 0.45),
    [activeLiquidColor, glowColor]
  );
  const liquidContrastColor = useMemo(
    () => pickReadableColor(activeLiquidColor, THEME_HIGHLIGHT_COLOR, THEME_TEXT_COLOR),
    [activeLiquidColor]
  );
  const resolvedTitleColor = useMemo(() => {
    const normalized = String(titleColor || '').trim();
    if (!normalized || matchesAnyColor(normalized, LEGACY_LIQUID_TITLE_COLORS)) {
      return THEME_TEXT_COLOR;
    }

    return normalized;
  }, [titleColor]);
  const resolvedValueColor = useMemo(() => {
    const normalized = String(valueColor || '').trim();
    if (!normalized || matchesAnyColor(normalized, LEGACY_LIQUID_VALUE_COLORS)) {
      return liquidContrastColor;
    }

    return normalized;
  }, [liquidContrastColor, valueColor]);
  const resolvedUnitColor = useMemo(() => {
    const normalized = String(unitColor || '').trim();
    if (!normalized || matchesAnyColor(normalized, LEGACY_LIQUID_UNIT_COLORS)) {
      return colorWithAlpha(liquidContrastColor, 0.84);
    }

    return normalized;
  }, [liquidContrastColor, unitColor]);
  const liquidSurfaceStrokeColor = useMemo(
    () => colorWithAlpha(mixColors(liquidBrightColor, THEME_HIGHLIGHT_COLOR, 0.14), isNearFull ? 0.46 : 0.24),
    [isNearFull, liquidBrightColor]
  );
  const liquidHighlightColor = useMemo(
    () => colorWithAlpha(THEME_HIGHLIGHT_COLOR, 0.1),
    []
  );

  const formattedValue = useMemo(
    () => formatDisplayValue(animatedValue, decimals),
    [animatedValue, decimals]
  );

  const unitText = useMemo(() => {
    if (!showUnit) return '';
    const normalizedUnit = String(unit || '').trim();
    return normalizedUnit;
  }, [showUnit, unit]);

  const valueSeparator = unitText === '%' ? '' : ' ';
  const displayTitle = useMemo(() => {
    const normalized = title == null ? '' : String(title);
    return normalized.trim() ? normalized : '';
  }, [title]);

  const resolvedCardBackground = useMemo(() => {
    const normalized = String(cardBackgroundColor || '').trim();
    if (!normalized || normalized === LEGACY_CARD_BACKGROUND) {
      return 'var(--header-background)';
    }
    return normalized;
  }, [cardBackgroundColor]);

  const containerStyle = useMemo(
    () => ({
      '--liquid-card-bg': resolvedCardBackground,
      '--liquid-title-color': resolvedTitleColor,
      '--liquid-value-color': resolvedValueColor,
      '--liquid-unit-color': resolvedUnitColor,
      '--liquid-glow-color': glowTone,
      '--liquid-scene-size': `${sceneSize}px`,
      '--liquid-title-shadow': colorWithAlpha(THEME_SHADOW_COLOR, 0.28),
      '--liquid-value-shadow-primary': colorWithAlpha(THEME_SHADOW_COLOR, 0.54),
      '--liquid-value-shadow-secondary': colorWithAlpha(THEME_SHADOW_COLOR, 0.22),
      '--liquid-bubble-color': colorWithAlpha(THEME_HIGHLIGHT_COLOR, 0.28),
      '--liquid-wave-shadow': colorWithAlpha(glowTone, 0.28),
      '--liquid-splash-shadow': colorWithAlpha(glowTone, 0.26),
    }),
    [
      glowTone,
      resolvedCardBackground,
      resolvedTitleColor,
      resolvedUnitColor,
      resolvedValueColor,
      sceneSize,
    ]
  );

  const cursorClass = draggable
    ? cursorState === 'grabbing'
      ? 'liquid-widget--grabbing'
      : 'liquid-widget--grab'
    : 'liquid-widget--auto';

  return (
    <div
      className={[
        'widget-container',
        'liquid-widget',
        cursorClass,
        transparentBackground ? 'liquid-widget--transparent' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={containerStyle}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={draggable}
      onDragStart={(event) => {
        const widgetId = event.target.closest('.widget')?.id;
        if (!widgetId) return;
        const rect = event.target.getBoundingClientRect();
        const offsetX = event.clientX - rect.x;
        const offsetY = event.clientY - rect.y;
        let dragData = `liquid;${widgetId};${offsetX};${offsetY};`;
        dragData += widgetId.startsWith('base') ? 'add' : 'move';
        event.dataTransfer.setData('text/plain', dragData);
      }}
    >
      {displayTitle ? (
        <div className="liquid-widget__header">
          <h3 className="liquid-widget__title">{displayTitle}</h3>
        </div>
      ) : null}

      <div className="liquid-widget__body">
        <div
          className={[
            'liquid-widget__scene',
            isNearFull ? 'liquid-widget__scene--full' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className="liquid-widget__sceneGlow" />
          <svg
            className="liquid-widget__svg"
            viewBox="0 0 120 120"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <defs>
              <clipPath id={clipId}>
                <path d={LIQUID_SHAPE_PATH} />
              </clipPath>
              <linearGradient id={fillGradientId} x1="18" y1="14" x2="102" y2="108" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={liquidBrightColor} />
                <stop offset="100%" stopColor={liquidDeepColor} />
              </linearGradient>
              <linearGradient
                id={fillGradientBackId}
                x1="14"
                y1="10"
                x2="104"
                y2="110"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={colorWithAlpha(liquidBrightColor, 0.78)} />
                <stop offset="100%" stopColor={colorWithAlpha(liquidDeepColor, 0.55)} />
              </linearGradient>
            </defs>

            <ellipse
              className="liquid-widget__backGlow"
              cx="60"
              cy="72"
              rx={26 + normalizedValue * 16}
              ry={12 + normalizedValue * 10}
              fill={colorWithAlpha(glowTone, 0.34)}
            />

            <g clipPath={`url(#${clipId})`}>
              <path
                d={LIQUID_SHAPE_PATH}
                className="liquid-widget__liquidShell"
                fill={colorWithAlpha(activeLiquidColor, 0.16)}
              />

              <g transform={`translate(0 ${surfaceY - 58})`}>
                <g
                  className={[
                    'liquid-widget__wave',
                    'liquid-widget__wave--back',
                    isNearFull ? 'liquid-widget__wave--full' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <path d={backWavePath} fill={`url(#${fillGradientBackId})`} />
                </g>
                <g
                  className={[
                    'liquid-widget__wave',
                    'liquid-widget__wave--front',
                    isNearFull ? 'liquid-widget__wave--full' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <path d={frontWavePath} fill={`url(#${fillGradientId})`} />
                  <path
                    d={frontWavePath}
                    fill="none"
                    stroke={liquidSurfaceStrokeColor}
                    strokeWidth="1.4"
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              </g>

              <ellipse
                className="liquid-widget__liquidHighlight"
                cx={42 + normalizedValue * 6}
                cy={40 + normalizedValue * 4}
                rx="18"
                ry="10"
                fill={liquidHighlightColor}
                transform="rotate(-22 42 40)"
              />

              {normalizedValue > 0.08 ? (
                <g className="liquid-widget__bubbleLayer">
                  <circle className="liquid-widget__bubble liquid-widget__bubble--one" cx="46" cy="74" r="3.2" />
                  <circle className="liquid-widget__bubble liquid-widget__bubble--two" cx="66" cy="80" r="2.6" />
                  <circle className="liquid-widget__bubble liquid-widget__bubble--three" cx="74" cy="66" r="2.2" />
                </g>
              ) : null}
            </g>

            <path
              d={LIQUID_SHAPE_PATH}
              fill="none"
              stroke={colorWithAlpha(liquidBrightColor, 0.18)}
              strokeWidth="1.4"
              vectorEffect="non-scaling-stroke"
            />

            {normalizedValue > 0.04 ? (
              <g
                className="liquid-widget__splash"
                transform={`translate(0 ${surfaceY - 52})`}
                opacity={0.9}
              >
                <ellipse
                  className="liquid-widget__splashDrop liquid-widget__splashDrop--one"
                  cx="77"
                  cy="50"
                  rx="5.5"
                  ry="2"
                  fill={colorWithAlpha(liquidBrightColor, 0.94)}
                  transform="rotate(-28 77 50)"
                />
                <ellipse
                  className="liquid-widget__splashDrop liquid-widget__splashDrop--two"
                  cx="84"
                  cy="44"
                  rx="2.2"
                  ry="2.2"
                  fill={colorWithAlpha(liquidBrightColor, 0.9)}
                />
              </g>
            ) : null}
          </svg>

          <div className="liquid-widget__value">
            <span className="liquid-widget__valueNumber">{formattedValue}</span>
            {unitText ? (
              <span className="liquid-widget__valueUnit">
                {valueSeparator}
                {unitText}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiquidWidget;
